/**
 * RGPD / Google Consent Mode v2 — centralized consent manager.
 *
 * Flow:
 * 1. On first load, consent defaults are set to "denied" via gtag('consent','default',…)
 * 2. A banner is shown to the user (CookieConsentBanner component)
 * 3. On user action, consent is updated via gtag('consent','update',…)
 * 4. Choice is persisted in localStorage
 *
 * GA4 script is loaded immediately but operates in cookieless/ping mode
 * until consent is granted (Google Consent Mode v2 behaviour).
 */

const CONSENT_KEY = "ouratrust_consent";

export type ConsentChoice = "granted" | "denied";

export interface ConsentState {
  analytics_storage: ConsentChoice;
  ad_storage: ConsentChoice;
  ad_user_data: ConsentChoice;
  ad_personalization: ConsentChoice;
}

const DENIED_STATE: ConsentState = {
  analytics_storage: "denied",
  ad_storage: "denied",
  ad_user_data: "denied",
  ad_personalization: "denied",
};

const GRANTED_STATE: ConsentState = {
  analytics_storage: "granted",
  ad_storage: "denied", // OuraTrust doesn't run ads — keep denied
  ad_user_data: "denied",
  ad_personalization: "denied",
};

/** Push consent default BEFORE gtag config — must be called first. */
export function setConsentDefaults(): void {
  if (typeof window === "undefined") return;

  (window as any).dataLayer = (window as any).dataLayer || [];
  function gtag(..._args: any[]) {
    (window as any).dataLayer.push(arguments);
  }
  (window as any).gtag = (window as any).gtag || gtag;

  // Check if user already made a choice
  const saved = getSavedConsent();

  (window as any).gtag("consent", "default", {
    analytics_storage: saved?.analytics_storage ?? "denied",
    ad_storage: "denied",
    ad_user_data: "denied",
    ad_personalization: "denied",
    wait_for_update: 500,
  });
}

/** Update consent after user action. */
export function updateConsent(choice: "accept" | "refuse"): void {
  if (typeof window === "undefined") return;

  const state = choice === "accept" ? GRANTED_STATE : DENIED_STATE;

  (window as any).gtag?.("consent", "update", { ...state });

  localStorage.setItem(CONSENT_KEY, JSON.stringify(state));
}

/** Read saved consent from localStorage. */
export function getSavedConsent(): ConsentState | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as ConsentState;
  } catch {
    return null;
  }
}

/** Whether the user has already made a consent choice. */
export function hasConsentChoice(): boolean {
  return getSavedConsent() !== null;
}
